import React from 'react';
import { BrowserRouter, Routes, Route, Navigate, Outlet } from 'react-router-dom';
import { AuthProvider, useAuth, isInvitedMember, getRoleName } from './context/AuthContext';
import { AdminAuthProvider, useAdminAuth } from './admin/context/AdminAuthContext';
import AdminLayout from './admin/layouts/AdminLayout';
import AdminLogin from './admin/pages/AdminLogin';
import AdminDashboard from './admin/pages/Dashboard';
import AdminUsers from './admin/pages/Users';
import AdminReports from './admin/pages/Reports';
import AdminSubscriptions from './admin/pages/Subscriptions';
import AdminLapsed from './admin/pages/Lapsed';
import AdminTickets from './admin/pages/Tickets';
import AdminSettings from './admin/pages/Settings';
import AdminOrders from './admin/pages/OrderManagement';
import Navbar from './components/Navbar';
import Footer from './components/Footer';
import ScrollToTop from './components/ScrollToTop';
import Home from './pages/website/Home';
import About from './pages/website/About';
import Pricing from './pages/website/Pricing';
import Contact from './pages/website/Contact';
import { Privacy, Terms, Cancellation, Shipping, GDPR } from './pages/website/Legal';
import ApiReference from './pages/website/ApiReference';
import FAQPage from './pages/website/FAQPage';
import LMSIntegration from './pages/website/LMSIntegration';
import { ForResearchers, ForInstitutions, ForPublishers, ForCorporate, ForIndividuals } from './pages/website/Audience';
import Register from './pages/auth/Register';
import Login from './pages/auth/Login';
import VerifyOTP from './pages/auth/VerifyOTP';
import ChoosePlan from './pages/ChoosePlan';
import SetupBranding from './pages/website/SetupBranding';
import ForgotPassword from './pages/auth/ForgotPassword';
import ResetPassword from './pages/auth/ResetPassword';
import DashboardLayout from './layouts/DashboardLayout';
import DashboardHome from './pages/dashboard/DashboardHome';
import Profile from './pages/dashboard/Profile';
import PlagiarismCheck from './pages/dashboard/PlagiarismCheck';
import AICheckPage from './pages/dashboard/AICheckPage';
import ReportView from './pages/ReportView';
import {
  MyReports, SubmissionHistory,
  UsagePage, SubscriptionPage,
  APIUsagePage, APIDocsPage,
  TicketsPage, InvoicesPage,
  SettingsPage, ChangePassword,
} from './pages/dashboard/DashboardPages';
import APIKeysPage from './pages/dashboard/APIKeysPage';
import BillingPage from './pages/dashboard/BillingPage';
import TeamManagement from './pages/dashboard/TeamManagement';
import AcceptInvite from './pages/auth/AcceptInvite';
import { RenewSubscription } from './admin/pages/Renewsubscription';
import TransactionHistory from './admin/pages/TransactionHistory';
import { apiFetch } from './services/api';

function Layout({ children }: { children: React.ReactNode }) {
  return (
    <div className="min-h-screen flex flex-col bg-white">
      <Navbar />
      <main className="flex-1">{children}</main>
      <Footer />
    </div>
  );
}

function Protected({ children }: { children: React.ReactNode }) {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="flex flex-col items-center">
          <div className="w-12 h-12 border-4 border-[#003c6c] border-t-transparent rounded-full animate-spin"></div>
          <p className="mt-4 text-sm font-medium text-gray-600">
            Manna Plag
          </p>
        </div>
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  return <>{children}</>;
}

function OwnerOnly({ children }: { children: React.ReactNode }) {
  const { user } = useAuth();
  const [/* planName */, setPlanName] = React.useState<string | null>(null);
  const [planLoading, setPlanLoading] = React.useState(true);

  React.useEffect(() => {
    let cancelled = false;
    apiFetch('/payments/my-subscription/')
      .then((data: any) => { if (!cancelled) setPlanName(data?.plan_name || ''); })
      .catch(() => { if (!cancelled) setPlanName(''); })
      .finally(() => { if (!cancelled) setPlanLoading(false); });
    return () => { cancelled = true; };
  }, [user]);

  if (planLoading) return null;
const roleLabel = getRoleName(user?.role)
  ? getRoleName(user?.role).charAt(0).toUpperCase() + getRoleName(user?.role).slice(1)
  : '';
  const isDemoPlan = (roleLabel || '').trim().toLowerCase() === 'demo';
  if (isInvitedMember(user?.role) && !isDemoPlan) return <Navigate to="/dashboard" replace />;
  return <>{children}</>;
}


function PublisherOnly({ children }: { children: React.ReactNode }) {
  const { user,loading } = useAuth();
  if (loading) return null;
  if (isInvitedMember(user?.role)) return <Navigate to="/dashboard" replace />;
  if (getRoleName(user?.role).toLowerCase() !== 'publisher') return <Navigate to="/dashboard" replace />;
  return <>{children}</>;
}

function GuestOnly({ children }: { children: React.ReactNode }) {
  const { user, loading, logout } = useAuth();
  const [loggingOut, setLoggingOut] = React.useState(false);
  const checkedInitialSession = React.useRef(false);

  React.useEffect(() => {
    if (loading) return;
    if (checkedInitialSession.current) return;
    checkedInitialSession.current = true;

    if (user) {
      setLoggingOut(true);
      logout().finally(() => setLoggingOut(false));
    }
  }, [loading, user, logout]);

  if (loading || loggingOut) return null;
  return <>{children}</>;
}

function AdminProtected({ children }: { children: React.ReactNode }) {
  const { admin, loading } = useAdminAuth();
  if (loading) return (
    <div className="min-h-screen flex items-center justify-center">
      <div className="w-6 h-6 border-2 border-navy border-t-transparent rounded-full animate-spin" />
    </div>
  );
  if (!admin) return <Navigate to="/superadmin/login" replace />;
  return <>{children}</>;
}

function AdminGuestOnly({ children }: { children: React.ReactNode }) {
  const { admin, loading } = useAdminAuth();
  if (loading) return null;
  if (admin) return <Navigate to="/superadmin" replace />;
  return <>{children}</>;
}

export default function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <ScrollToTop />
        <Routes>

          {/* Standalone auth + onboarding */}
          <Route path="/register" element={<GuestOnly><Register /></GuestOnly>} />
          <Route path="/login" element={<GuestOnly><Login /></GuestOnly>} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/verify-otp" element={<VerifyOTP />} />
          <Route path="/choose-plan" element={<Protected><ChoosePlan /></Protected>} />
          <Route path="/setup-branding" element={<Protected><SetupBranding /></Protected>} />
          <Route path="/accept-invite/:token" element={<AcceptInvite />} />
          <Route path="/reports/:reportId" element={<Protected><ReportView /></Protected>} />

          {/* Dashboard */}
          <Route path="/dashboard" element={<Protected><DashboardLayout /></Protected>}>
            <Route index element={<DashboardHome />} />
            <Route path="plagiarism" element={<PlagiarismCheck />} />
            <Route path="ai-check" element={<AICheckPage />} />
            <Route path="reports" element={<MyReports />} />
            <Route path="history" element={<SubmissionHistory />} />
            <Route path="team" element={<OwnerOnly><TeamManagement /></OwnerOnly>} />
            <Route path="api-keys" element={<PublisherOnly><APIKeysPage /></PublisherOnly>} />
            <Route path="api-usage" element={<OwnerOnly><APIUsagePage /></OwnerOnly>} />
            <Route path="api-docs" element={<OwnerOnly><APIDocsPage /></OwnerOnly>} />
            <Route path="usage" element={<UsagePage />} />
            <Route path="subscription" element={<OwnerOnly><SubscriptionPage /></OwnerOnly>} />
            <Route path="billing" element={<OwnerOnly><BillingPage /></OwnerOnly>} />
            <Route path="invoices" element={<OwnerOnly><InvoicesPage /></OwnerOnly>} />
            <Route path="tickets" element={<TicketsPage />} />
            <Route path="settings" element={<SettingsPage />} />
            <Route path="profile" element={<Profile />} />
            <Route path="password" element={<ChangePassword />} />
          </Route>

          {/* Admin panel — isolated auth session, own layout */}
          <Route path="/superadmin/*" element={<AdminAuthProvider><Outlet /></AdminAuthProvider>}>
            <Route path="login" element={<AdminGuestOnly><AdminLogin /></AdminGuestOnly>} />
            <Route element={<AdminProtected><AdminLayout /></AdminProtected>}>
              <Route index element={<AdminDashboard />} />
              <Route path="users" element={<AdminUsers />} />
              <Route path="reports" element={<AdminReports />} />
              <Route path="orders" element={<AdminOrders />} />
              <Route path="subscriptions" element={<AdminSubscriptions />} />
              <Route path="renewals" element={<RenewSubscription />} />
              <Route path="lapsed" element={<AdminLapsed />} />
              <Route path="transactions" element={<TransactionHistory />} />
              <Route path="tickets" element={<AdminTickets />} />
              <Route path="settings" element={<AdminSettings />} />
            </Route>
          </Route>

          {/* Public */}
          <Route path="/*" element={
            <Layout>
              <Routes>
                <Route path="/" element={<Home />} />
                <Route path="/about" element={<About />} />
                <Route path="/pricing" element={<Pricing />} />
                <Route path="/contact" element={<Contact />} />
                <Route path="/researchers" element={<ForResearchers />} />
                <Route path="/institutions" element={<ForInstitutions />} />
                <Route path="/publishers" element={<ForPublishers />} />
                <Route path="/corporate" element={<ForCorporate />} />
                <Route path="/individuals" element={<ForIndividuals />} />
                <Route path="/privacy" element={<Privacy />} />
                <Route path="/terms" element={<Terms />} />
                <Route path="/cancellation" element={<Cancellation />} />
                <Route path="/shipping" element={<Shipping />} />
                <Route path="/gdpr" element={<GDPR />} />
                <Route path="/api-reference" element={<ApiReference />} />
                <Route path="/faqs" element={<FAQPage />} />
                <Route path="/lms-integration" element={<LMSIntegration />} />
              </Routes>
            </Layout>
          } />

        </Routes>
      </BrowserRouter>
    </AuthProvider>
  );
}
